/**
 * KB Gender Experience
 * Versión consolidada
 *
 * Fuente única de verdad para:
 * - Entry Gate.
 * - Selector del header.
 * - Botones de KB-hero-slider.
 * - Visibilidad reactiva de Home y landing pages.
 */

(function () {
  'use strict';

  var STORAGE_KEY = 'kb_affinity_v1';
  var SESSION_KEY = 'kb_affinity_session_v1';
  var CONFIG_KEY = 'kb_affinity_config_v1';

  var STORAGE_VERSION = 1;

  var DEFAULT_PERSISTENCE_DAYS = 90;
  var MIN_PERSISTENCE_DAYS = 1;
  var MAX_PERSISTENCE_DAYS = 365;

  var DEFAULT_TRANSITION_DURATION = 480;
  var MIN_TRANSITION_DURATION = 0;
  var MAX_TRANSITION_DURATION = 2000;

  var VALID_PREFERENCES = ['woman', 'man'];
  var VALID_STATES = ['woman', 'man', 'none'];

  var currentState = null;
  var initialized = false;
  var affinityChangeTimer = null;

  var previousFocusByGate =
    typeof WeakMap === 'function'
      ? new WeakMap()
      : null;

  var gateTimerByGate =
    typeof WeakMap === 'function'
      ? new WeakMap()
      : null;

  /* =====================================================
     UTILIDADES GENERALES
     ===================================================== */

  function isDesignMode() {
    return Boolean(
      window.Shopify &&
      window.Shopify.designMode
    );
  }

  function isTrue(value) {
    return (
      value === true ||
      value === 'true' ||
      value === '1'
    );
  }

  function normalizeInteger(
    value,
    fallback,
    minimum,
    maximum
  ) {
    var parsed = parseInt(value, 10);

    if (!Number.isFinite(parsed)) {
      return fallback;
    }

    return Math.min(
      Math.max(parsed, minimum),
      maximum
    );
  }

  function isValidPreference(value) {
    return VALID_PREFERENCES.indexOf(value) !== -1;
  }

  function isValidState(value) {
    return VALID_STATES.indexOf(value) !== -1;
  }

  function createCustomEvent(name, detail) {
    try {
      return new CustomEvent(name, {
        bubbles: true,
        detail: detail
      });
    } catch (error) {
      var event =
        document.createEvent('CustomEvent');

      event.initCustomEvent(
        name,
        true,
        false,
        detail
      );

      return event;
    }
  }

  /* =====================================================
     WEB STORAGE SEGURO
     ===================================================== */

  function getStorage(type) {
    try {
      if (type === 'local') {
        return window.localStorage;
      }

      if (type === 'session') {
        return window.sessionStorage;
      }
    } catch (error) {
      return null;
    }

    return null;
  }

  function storageGet(type, key) {
    var storage = getStorage(type);

    if (!storage) {
      return null;
    }

    try {
      return storage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function storageSet(type, key, value) {
    var storage = getStorage(type);

    if (!storage) {
      return false;
    }

    try {
      storage.setItem(key, value);
      return true;
    } catch (error) {
      return false;
    }
  }

  function storageRemove(type, key) {
    var storage = getStorage(type);

    if (!storage) {
      return false;
    }

    try {
      storage.removeItem(key);
      return true;
    } catch (error) {
      return false;
    }
  }

  /* =====================================================
     CONFIGURACIÓN DE PERSISTENCIA
     ===================================================== */

  function readStoredConfig() {
    var raw = storageGet('local', CONFIG_KEY);

    if (!raw) {
      return null;
    }

    try {
      var config = JSON.parse(raw);

      if (
        !config ||
        typeof config !== 'object' ||
        config.version !== STORAGE_VERSION
      ) {
        storageRemove('local', CONFIG_KEY);
        return null;
      }

      return {
        persistenceDays: normalizeInteger(
          config.persistenceDays,
          DEFAULT_PERSISTENCE_DAYS,
          MIN_PERSISTENCE_DAYS,
          MAX_PERSISTENCE_DAYS
        )
      };
    } catch (error) {
      storageRemove('local', CONFIG_KEY);
      return null;
    }
  }

  function storeGateConfig(gate) {
    if (!gate) {
      return;
    }

    var days = normalizeInteger(
      gate.getAttribute(
        'data-kb-persistence-days'
      ),
      DEFAULT_PERSISTENCE_DAYS,
      MIN_PERSISTENCE_DAYS,
      MAX_PERSISTENCE_DAYS
    );

    storageSet(
      'local',
      CONFIG_KEY,
      JSON.stringify({
        persistenceDays: days,
        version: STORAGE_VERSION
      })
    );
  }

  function getPersistenceDays(contextElement) {
    var element = null;

    if (
      contextElement &&
      typeof contextElement.closest === 'function'
    ) {
      element = contextElement.closest(
        '[data-kb-persistence-days]'
      );
    }

    if (!element) {
      element = document.querySelector(
        '[data-kb-persistence-days]'
      );
    }

    if (element) {
      return normalizeInteger(
        element.getAttribute(
          'data-kb-persistence-days'
        ),
        DEFAULT_PERSISTENCE_DAYS,
        MIN_PERSISTENCE_DAYS,
        MAX_PERSISTENCE_DAYS
      );
    }

    var config = readStoredConfig();

    return config
      ? config.persistenceDays
      : DEFAULT_PERSISTENCE_DAYS;
  }

  /* =====================================================
     PREFERENCIA Y ESTADO GLOBAL
     ===================================================== */

  function getPersistentPreference() {
    var raw = storageGet('local', STORAGE_KEY);

    if (!raw) {
      return null;
    }

    try {
      var stored = JSON.parse(raw);

      if (
        !stored ||
        typeof stored !== 'object' ||
        stored.version !== STORAGE_VERSION ||
        !isValidPreference(stored.value) ||
        typeof stored.expiresAt !== 'number' ||
        !Number.isFinite(stored.expiresAt)
      ) {
        storageRemove('local', STORAGE_KEY);
        return null;
      }

      if (stored.expiresAt <= Date.now()) {
        storageRemove('local', STORAGE_KEY);
        return null;
      }

      return {
        value: stored.value,
        expiresAt: stored.expiresAt
      };
    } catch (error) {
      storageRemove('local', STORAGE_KEY);
      return null;
    }
  }

  function hasSessionAll() {
    return (
      storageGet('session', SESSION_KEY) ===
      'all'
    );
  }

  function resolveState() {
    var preference =
      getPersistentPreference();

    return preference
      ? preference.value
      : 'woman';
  }

  function getActionSource(element) {
    if (!element) {
      return 'api';
    }

    var sourceElement = null;

    if (
      typeof element.closest === 'function'
    ) {
      sourceElement = element.closest(
        '[data-kb-source]'
      );
    }

    if (sourceElement) {
      return (
        sourceElement.getAttribute(
          'data-kb-source'
        ) || 'unknown'
      );
    }

    if (element.closest('[data-kb-gate]')) {
      return 'gate';
    }

    if (
      element.closest('[data-kb-switcher]')
    ) {
      return 'header';
    }

    return 'button';
  }

  function syncSwitchers(state) {
    var buttons = document.querySelectorAll(
    '[data-kb-action="select"]' +
    '[data-kb-value]' +
    '[aria-pressed]'
  );

    Array.prototype.forEach.call(
      buttons,
      function (button) {
        var value =
          button.getAttribute('data-kb-value');

        button.setAttribute(
          'aria-pressed',
          value === state ? 'true' : 'false'
        );
      }
    );
  }

  function markAffinityChanging() {
    document.documentElement.classList.add(
      'kb-affinity-is-changing'
    );

    window.clearTimeout(
      affinityChangeTimer
    );

    affinityChangeTimer =
      window.setTimeout(function () {
        document.documentElement.classList.remove(
          'kb-affinity-is-changing'
        );
      }, 80);
  }

  function dispatchStateChange(
    state,
    source
  ) {
    document.dispatchEvent(
      createCustomEvent(
        'kb:gender-change',
        {
          gender: state,
          source: source || 'unknown'
        }
      )
    );
  }

  function applyState(
    state,
    source,
    forceEvent
  ) {
    if (!isValidState(state)) {
      state = 'none';
    }

    var changed = currentState !== state;

    currentState = state;

    document.documentElement.setAttribute(
      'data-kb-gender',
      state
    );

    syncSwitchers(state);

    if (changed) {
      markAffinityChanging();
    }

    if (changed || forceEvent) {
      dispatchStateChange(state, source);
    }
  }

  function setPersistentPreference(
    value,
    contextElement,
    source
  ) {
    if (!isValidPreference(value)) {
      return false;
    }

    var days =
      getPersistenceDays(contextElement);

    var expiresAt =
      Date.now() +
      days * 24 * 60 * 60 * 1000;

    var saved = storageSet(
      'local',
      STORAGE_KEY,
      JSON.stringify({
        value: value,
        expiresAt: expiresAt,
        version: STORAGE_VERSION
      })
    );

    storageRemove('session', SESSION_KEY);

    applyState(
      value,
      source || 'selection'
    );

    return saved;
  }

  function selectAllForSession(source) {
    storageRemove('local', STORAGE_KEY);

    storageSet(
      'session',
      SESSION_KEY,
      'all'
    );

    applyState(
      'none',
      source || 'selection'
    );
  }

  /* =====================================================
     ENTRY GATE
     ===================================================== */

  function getTransitionDuration(gate) {
    if (!gate) {
      return DEFAULT_TRANSITION_DURATION;
    }

    return normalizeInteger(
      gate.getAttribute(
        'data-kb-transition-duration'
      ),
      DEFAULT_TRANSITION_DURATION,
      MIN_TRANSITION_DURATION,
      MAX_TRANSITION_DURATION
    );
  }

  function prefersReducedMotion() {
    return Boolean(
      window.matchMedia &&
      window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches
    );
  }

  function clearGateTimer(gate) {
    if (
      !gateTimerByGate ||
      !gateTimerByGate.has(gate)
    ) {
      return;
    }

    window.clearTimeout(
      gateTimerByGate.get(gate)
    );

    gateTimerByGate.delete(gate);
  }

  function setGateTimer(
    gate,
    callback,
    delay
  ) {
    clearGateTimer(gate);

    var timer = window.setTimeout(
      callback,
      delay
    );

    if (gateTimerByGate) {
      gateTimerByGate.set(gate, timer);
    }
  }

  function lockScroll() {
    document.documentElement.classList.add(
      'kb-gender-scroll-lock'
    );

    if (document.body) {
      document.body.classList.add(
        'kb-gender-scroll-lock'
      );
    }
  }

  function unlockScrollIfPossible() {
    var visibleGate = document.querySelector(
      '[data-kb-gate]' +
      '.is-visible:not([hidden])'
    );

    if (visibleGate) {
      return;
    }

    document.documentElement.classList.remove(
      'kb-gender-scroll-lock'
    );

    if (document.body) {
      document.body.classList.remove(
        'kb-gender-scroll-lock'
      );
    }
  }

  function getFocusableElements(gate) {
    if (!gate) {
      return [];
    }

    var selector = [
      'button:not([disabled])',
      'a[href]',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',');

    return Array.prototype.filter.call(
      gate.querySelectorAll(selector),
      function (element) {
        return (
          !element.hasAttribute('hidden') &&
          element.getAttribute(
            'aria-hidden'
          ) !== 'true'
        );
      }
    );
  }

  function resetGateVisualState(gate) {
    gate.classList.remove(
      'is-visible',
      'is-closing',
      'is-selecting'
    );

    gate.removeAttribute('data-kb-busy');

    Array.prototype.forEach.call(
      gate.querySelectorAll(
        '.kb-gender-gate__panel' +
        '.is-selected'
      ),
      function (panel) {
        panel.classList.remove('is-selected');
      }
    );
  }

  function openGate(gate, force) {
    if (!gate) {
      return;
    }

    if (
      !force &&
      !isTrue(
        gate.getAttribute('data-kb-enabled')
      )
    ) {
      hideGateImmediately(gate, false);
      return;
    }

    if (
      gate.classList.contains('is-visible') &&
      !gate.hidden
    ) {
      return;
    }

    clearGateTimer(gate);

    if (previousFocusByGate) {
      previousFocusByGate.set(
        gate,
        document.activeElement
      );
    }

    gate.style.setProperty(
      '--kb-gate-transition-duration',
      getTransitionDuration(gate) + 'ms'
    );

    resetGateVisualState(gate);

    gate.hidden = false;
    gate.removeAttribute('hidden');
    gate.setAttribute('aria-hidden', 'false');

    lockScroll();

    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        if (!gate.isConnected) {
          return;
        }

        gate.classList.add('is-visible');

        var focusTarget =
          gate.querySelector(
            '[data-kb-autofocus]'
          ) ||
          getFocusableElements(gate)[0];

        if (
          focusTarget &&
          typeof focusTarget.focus ===
            'function'
        ) {
          try {
            focusTarget.focus({
              preventScroll: true
            });
          } catch (error) {
            focusTarget.focus();
          }
        }
      });
    });
  }

  function hideGateImmediately(
    gate,
    restoreFocus
  ) {
    if (!gate) {
      return;
    }

    clearGateTimer(gate);
    resetGateVisualState(gate);

    gate.setAttribute('aria-hidden', 'true');
    gate.hidden = true;

    unlockScrollIfPossible();

    if (
      restoreFocus &&
      previousFocusByGate &&
      previousFocusByGate.has(gate)
    ) {
      var previousFocus =
        previousFocusByGate.get(gate);

      if (
        previousFocus &&
        previousFocus.isConnected &&
        typeof previousFocus.focus ===
          'function'
      ) {
        try {
          previousFocus.focus({
            preventScroll: true
          });
        } catch (error) {
          previousFocus.focus();
        }
      }

      previousFocusByGate.delete(gate);
    }
  }

  function closeGate(gate, selectedPanel) {
    if (
      !gate ||
      gate.getAttribute(
        'data-kb-busy'
      ) === 'true'
    ) {
      return;
    }

    gate.setAttribute(
      'data-kb-busy',
      'true'
    );

    if (selectedPanel) {
      selectedPanel.classList.add(
        'is-selected'
      );

      gate.classList.add('is-selecting');
    }

    var duration = prefersReducedMotion()
      ? 0
      : getTransitionDuration(gate);

    window.requestAnimationFrame(function () {
      if (!gate.isConnected) {
        return;
      }

      gate.classList.add('is-closing');

      setGateTimer(
        gate,
        function () {
          if (!gate.isConnected) {
            unlockScrollIfPossible();
            return;
          }

          hideGateImmediately(gate, true);
        },
        duration + 40
      );
    });
  }

  function shouldOpenGate(gate) {
  if (!gate) {
    return false;
  }

  /*
   * En el Theme Editor el gate solo se abre
   * explícitamente desde handleSectionSelect().
   */
  if (isDesignMode()) {
    return false;
  }

  return Boolean(
    isTrue(
      gate.getAttribute('data-kb-enabled')
    ) &&
    !getPersistentPreference() &&
    !hasSessionAll()
  );
}

  function updateGateVisibility(gate) {
    if (!gate) {
      return;
    }

    if (shouldOpenGate(gate)) {
      openGate(gate, false);
    } else {
      hideGateImmediately(gate, false);
    }
  }

  function initializeGate(gate) {
    storeGateConfig(gate);

    gate.style.setProperty(
      '--kb-gate-transition-duration',
      getTransitionDuration(gate) + 'ms'
    );

    updateGateVisibility(gate);
  }

  function getGates(context) {
    var root = context || document;
    var gates = [];

    if (
      root.matches &&
      root.matches('[data-kb-gate]')
    ) {
      gates.push(root);
    }

    if (root.querySelectorAll) {
      gates = gates.concat(
        Array.prototype.slice.call(
          root.querySelectorAll(
            '[data-kb-gate]'
          )
        )
      );
    }

    return gates;
  }

  function initializeContext(context) {
    getGates(context).forEach(
      initializeGate
    );

    syncSwitchers(
      currentState || resolveState()
    );
  }

  function hideAllGates(exceptGate) {
    getGates(document).forEach(
      function (gate) {
        if (gate !== exceptGate) {
          hideGateImmediately(gate, false);
        }
      }
    );
  }

  /* =====================================================
     INTERACCIONES OVERLAY / HERO / HEADER
     ===================================================== */

  function getActionElement(target) {
    var element =
      target && target.nodeType === 1
        ? target
        : target
          ? target.parentElement
          : null;

    if (
      !element ||
      typeof element.closest !== 'function'
    ) {
      return null;
    }

    return element.closest(
      '[data-kb-action]'
    );
  }

  function handleDocumentClick(event) {
    var actionElement =
      getActionElement(event.target);

    if (!actionElement) {
      return;
    }

    var action =
      actionElement.getAttribute(
        'data-kb-action'
      );

    var gate = actionElement.closest(
      '[data-kb-gate]'
    );

    var source =
      getActionSource(actionElement);

    if (action === 'select') {
  var value =
    actionElement.getAttribute(
      'data-kb-value'
    );

  if (!isValidPreference(value)) {
    return;
  }

  event.preventDefault();

  if (currentState !== value) {

    window.dataLayer = window.dataLayer || [];

    window.dataLayer.push({
      event: 'gender_selector_change',
      previous_gender: currentState,
      gender_selected: value,
      selector_source: source
    });

  }

  setPersistentPreference(
    value,
    actionElement,
    source
  );

      if (gate) {
        hideAllGates(gate);

        closeGate(
          gate,
          actionElement.closest(
            '.kb-gender-gate__panel'
          )
        );
      } else {
        hideAllGates(null);
      }

      return;
    }

    if (action === 'all') {
      event.preventDefault();

      selectAllForSession(source);

      if (gate) {
        hideAllGates(gate);
        closeGate(gate, null);
      } else {
        hideAllGates(null);
      }
    }
  }

  function handleDocumentKeydown(event) {
    if (event.key !== 'Tab') {
      return;
    }

    var gate = document.querySelector(
      '[data-kb-gate]' +
      '.is-visible:not([hidden])'
    );

    if (!gate) {
      return;
    }

    var focusable =
      getFocusableElements(gate);

    if (!focusable.length) {
      event.preventDefault();
      return;
    }

    var first = focusable[0];
    var last =
      focusable[focusable.length - 1];

    if (
      event.shiftKey &&
      document.activeElement === first
    ) {
      event.preventDefault();
      last.focus();
    } else if (
      !event.shiftKey &&
      document.activeElement === last
    ) {
      event.preventDefault();
      first.focus();
    }
  }

  function handleStorageChange(event) {
    if (
      event.key !== STORAGE_KEY &&
      event.key !== CONFIG_KEY &&
      event.key !== null
    ) {
      return;
    }

    applyState(
      resolveState(),
      'storage'
    );

    getGates(document).forEach(
      updateGateVisibility
    );
  }

  /* =====================================================
     SHOPIFY THEME EDITOR
     ===================================================== */

  function getSectionWrapper(element) {
    if (!element || !element.classList) {
      return null;
    }

    if (
      element.classList.contains(
        'shopify-section'
      )
    ) {
      return element;
    }

    return typeof element.closest ===
      'function'
      ? element.closest('.shopify-section')
      : null;
  }

  function setEditorSelection(
    element,
    selected
  ) {
    if (!isDesignMode()) {
      return;
    }

    var wrapper =
      getSectionWrapper(element);

    if (wrapper) {
      wrapper.classList.toggle(
        'kb-affinity-editor-selected',
        Boolean(selected)
      );
    }
  }

  function findGate(element) {
    if (!element) {
      return null;
    }

    if (
      element.matches &&
      element.matches('[data-kb-gate]')
    ) {
      return element;
    }

    return element.querySelector
      ? element.querySelector('[data-kb-gate]')
      : null;
  }

  function dispatchSectionRefresh(
    event,
    source
  ) {
    document.dispatchEvent(
      createCustomEvent(
        'kb:section-refresh',
        {
          sectionId:
            event.detail &&
            event.detail.sectionId
              ? event.detail.sectionId
              : null,
          source: source,
          element:
            getSectionWrapper(event.target)
        }
      )
    );
  }

  function handleSectionLoad(event) {
    setEditorSelection(
      event.target,
      false
    );

    initializeContext(event.target);

    dispatchSectionRefresh(
      event,
      'theme-editor-load'
    );
  }

  function handleSectionSelect(event) {
    if (!isDesignMode()) {
      return;
    }

    setEditorSelection(
      event.target,
      true
    );

    var gate = findGate(event.target);

    if (
      gate &&
      isTrue(
        gate.getAttribute(
          'data-kb-editor-preview'
        )
      )
    ) {
      openGate(gate, true);
    }

    dispatchSectionRefresh(
      event,
      'theme-editor-select'
    );
  }

  function handleSectionDeselect(event) {
    if (!isDesignMode()) {
      return;
    }

    setEditorSelection(
      event.target,
      false
    );

    var gate = findGate(event.target);

    if (gate) {
      hideGateImmediately(gate, false);
    }

    dispatchSectionRefresh(
      event,
      'theme-editor-deselect'
    );
  }

  function handleSectionUnload(event) {
    setEditorSelection(
      event.target,
      false
    );

    var gate = findGate(event.target);

    if (gate) {
      hideGateImmediately(gate, false);
    }
  }

  function handleBlockSelect(event) {
    if (!isDesignMode()) {
      return;
    }

    setEditorSelection(
      event.target,
      true
    );

    dispatchSectionRefresh(
      event,
      'theme-editor-block-select'
    );
  }

  function handleBlockDeselect(event) {
    if (!isDesignMode()) {
      return;
    }

    setEditorSelection(
      event.target,
      false
    );

    dispatchSectionRefresh(
      event,
      'theme-editor-block-deselect'
    );
  }

  /* =====================================================
     API PÚBLICA
     ===================================================== */

  function resetPreference() {
    storageRemove('local', STORAGE_KEY);
    storageRemove('session', SESSION_KEY);

    applyState('none', 'reset');

    getGates(document).forEach(
      updateGateVisibility
    );
  }

  window.KBGender = {
    get: function () {
      return currentState || resolveState();
    },

    select: function (value, source) {
      if (!isValidPreference(value)) {
        return false;
      }

      var saved =
        setPersistentPreference(
          value,
          null,
          source || 'api'
        );

      hideAllGates(null);

      return saved;
    },

    selectAll: function (source) {
      selectAllForSession(
        source || 'api'
      );

      hideAllGates(null);
    },

    reset: function () {
      resetPreference();
    },

    refresh: function () {
      applyState(
        resolveState(),
        'refresh',
        true
      );

      initializeContext(document);
    }
  };

  /* =====================================================
     INICIALIZACIÓN ÚNICA
     ===================================================== */

  function initialize() {
    if (initialized) {
      initializeContext(document);
      return;
    }

    initialized = true;

    applyState(
      resolveState(),
      'initialization',
      true
    );

    document.addEventListener(
      'click',
      handleDocumentClick
    );

    document.addEventListener(
      'keydown',
      handleDocumentKeydown
    );

    window.addEventListener(
      'storage',
      handleStorageChange
    );

    document.addEventListener(
      'shopify:section:load',
      handleSectionLoad
    );

    document.addEventListener(
      'shopify:section:select',
      handleSectionSelect
    );

    document.addEventListener(
      'shopify:section:deselect',
      handleSectionDeselect
    );

    document.addEventListener(
      'shopify:section:unload',
      handleSectionUnload
    );

    document.addEventListener(
      'shopify:block:select',
      handleBlockSelect
    );

    document.addEventListener(
      'shopify:block:deselect',
      handleBlockDeselect
    );

    initializeContext(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initialize,
      { once: true }
    );
  } else {
    initialize();
  }
})();